/**
 * TRITAL Care corporate GA4 — narrow, privacy-by-design recruiting analytics.
 *
 * Scope: corporate root/apex only, on approved public marketing/job routes.
 * Does NOT activate for ShiftBridge, Orbit, Nexus, dashboards, checklists or
 * applicant-specific URLs. Never pass FormData, names, email, phone, resumes,
 * callback preferences, ZIP, IP, raw query strings or free-form search terms.
 *
 * Property: G-CWX1KJ7ZTC. Analytics is never proof of inbox delivery;
 * "generate_lead" fires only after the existing intake API reports success.
 */
export const GA4_MEASUREMENT_ID = "G-CWX1KJ7ZTC";

let started = false;
// GA4 Enhanced Measurement already observes SPA history changes for this
// property. Emit only the first page_view ourselves; do not double-count
// later React Router transitions.
let initialPageViewSent = false;

export function isCorporateHost() {
  if (typeof window === "undefined") return false;
  return ["www.tritalcare.com", "tritalcare.com"].includes(window.location.hostname.toLowerCase());
}

/** Public paths with intentionally non-identifying URL segments only. */
export function isTrackableCorporatePath(pathname) {
  if (typeof pathname !== "string") return false;
  if (pathname === "/") return true;
  if (/^\/(?:jobs|hot-jobs)(?:\/[a-z0-9-]{2,140})?\/?$/.test(pathname)) return true;
  return new Set([
    "/clinicians", "/employers", "/services", "/contact", "/about",
    "/apply", "/platforms", "/diversity-and-inclusion", "/blog", "/careers",
  ]).has(pathname);
}

function pageLabel(pathname) {
  if (pathname === "/") return "TRITAL Care Corporate";
  if (pathname === "/jobs") return "Verified Healthcare Jobs";
  if (pathname.startsWith("/jobs/")) return "Individual Verified Healthcare Job";
  if (pathname === "/hot-jobs") return "Hot Jobs Recruiting Directory";
  if (pathname.startsWith("/hot-jobs/")) return "Hot Jobs Recruiting Detail";
  return "TRITAL Care " + pathname.slice(1).replace(/[^a-z-]/g, " ").slice(0, 60);
}

function safeContext(pathname) {
  if (!isCorporateHost() || !isTrackableCorporatePath(pathname)) return null;
  // No query string or fragment: these can contain candidate or campaign PII.
  return {
    page_location: window.location.origin + pathname,
    page_title: pageLabel(pathname),
    // Restrict potentially identifying referral URLs to their origin only.
    page_referrer: (() => {
      try { return document.referrer ? new URL(document.referrer).origin : ""; }
      catch (_) { return ""; }
    })(),
  };
}

export function initCorporateGA4(pathname) {
  const page = safeContext(pathname);
  if (!page || started) return;
  started = true;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
  window.gtag("js", new Date());
  // Override browser URLs at the tag configuration boundary too:
  // GA4 Enhanced Measurement (e.g., automatic form_start events) must never
  // receive ?apply, ?utm_* or any untrusted URL query values.
  window.gtag("set", {
    page_location: page.page_location,
    page_title: page.page_title,
    page_referrer: page.page_referrer,
  });
  window.gtag("config", GA4_MEASUREMENT_ID, {
    send_page_view: false,
    page_location: page.page_location,
    page_title: page.page_title,
    page_referrer: page.page_referrer,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    anonymize_ip: true,
  });

  if (!document.querySelector('script[data-trital-corporate-ga4="1"]')) {
    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-trital-corporate-ga4", "1");
    script.src = "https://www.googletagmanager.com/gtag/js?id=" + GA4_MEASUREMENT_ID;
    document.head.appendChild(script);
  }
}

function cleanJobMetadata(values) {
  const safe = {};
  if (!values || typeof values !== "object") return safe;
  // Only company-curated, non-identifying dimensions pass the GA4 boundary.
  const id = String(values.job_id || "");
  if (/^[A-Za-z0-9-]{3,140}$/.test(id)) safe.job_id = id;
  if (["Nursing", "Allied", "Therapy", "PRN", "Physicians & APPs"].includes(values.job_category)) {
    safe.job_category = values.job_category;
  }
  if (/^[A-Z]{2}$/.test(values.job_state || "")) safe.job_state = values.job_state;
  if (["verified_job", "hot_jobs"].includes(values.application_source)) {
    safe.application_source = values.application_source;
  }
  if (typeof values.has_resume === "boolean") safe.has_resume = values.has_resume;
  return safe;
}

export function trackCorporatePageView(pathname) {
  const page = safeContext(pathname);
  if (!page) return;
  initCorporateGA4(pathname);
  // First view only: GA4 Enhanced Measurement automatically records later
  // browser-history changes. This also dedupes React StrictMode mounts.
  if (initialPageViewSent) return;
  initialPageViewSent = true;
  window.gtag("event", "page_view", { ...page, send_to: GA4_MEASUREMENT_ID });
}

const TRACKED_EVENTS = new Set([
  "hot_jobs_directory_view",
  "verified_jobs_directory_view",
  "verified_job_detail_view",
  "verified_job_apply_started",
  "hot_jobs_application_started",
  "hot_jobs_application_submitted",
  "generate_lead",
  "native_job_apply_view",
  "native_job_application_submitted",
  "hot_job_detail_view",
]);

export function trackCorporateEvent(eventName, metadata = {}) {
  if (!TRACKED_EVENTS.has(eventName)) return;
  const page = safeContext(window.location.pathname);
  if (!page) return;
  initCorporateGA4(window.location.pathname);
  window.gtag("event", eventName, {
    ...page,
    ...cleanJobMetadata(metadata),
    send_to: GA4_MEASUREMENT_ID,
  });
}
